import { useMemo } from 'react';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { GET_MATCHING_RULES } from './matching-rules-model.gql';
import { GetMatchingRules } from './__generated__/GetMatchingRules';

export interface MatchingRuleSession {
  matchingRuleSessionId: string;
  name: string;
  isFlexible: boolean;
  position: number;
  reportType: { reportTypeId: string; name: string } | null;
}

export interface MatchingRule {
  matchingRuleId: string;
  name: string;
  enabled: boolean;
  ruleOrder: number;
  matchOnAvailability: boolean;
  minimumScore: number;
  action: string;
  actionScheduling: string;
  actionFrequency: string;
  actionDurationMonths: number;
  applicantRole: { activityRoleId: string; name: string };
  counterpartRole: { activityRoleId: string; name: string };
  program: { programId: string; name: string } | null;
  sessions: MatchingRuleSession[];
}

interface ApplicationRoleLike {
  role: { activityRoleId: string; name: string };
}

export const useMatchingRules = () => {
  const { data, loading } = useCampfireQuery<GetMatchingRules, {}>(GET_MATCHING_RULES);

  const enabled = data?.vm.configItem?.configItemValue?.flagValue ?? data?.vm.configItem?.defaultValue ?? false;

  const rules = useMemo(() => {
    if (!enabled) {
      return [] as MatchingRule[];
    }

    return [...((data?.vm.matchingRules ?? []) as MatchingRule[])]
      .filter((rule) => rule.enabled)
      .sort((a, b) => a.ruleOrder - b.ruleOrder);
  }, [data, enabled]);

  /**
   * The first rule whose applicant role the person actually holds decides which
   * counterparts we suggest. Rules are ordered, so an admin controls precedence.
   */
  const findRuleForRoles = (applicationRoles: ApplicationRoleLike[] | null | undefined) => {
    if (!applicationRoles) {
      return null;
    }

    const rule = rules.find((candidate) =>
      applicationRoles.some((ar) => ar.role.activityRoleId === candidate.applicantRole.activityRoleId)
    );

    if (!rule) {
      return null;
    }

    const held = applicationRoles.find((ar) => ar.role.activityRoleId === rule.applicantRole.activityRoleId);

    return held ? { rule, matchedRole: held.role } : null;
  };

  return { enabled, rules, findRuleForRoles, loading };
};
